import * as React from 'react';

import { createReactRenderer } from 'helpers/react-renderer';

import DiscountRuleDropdown from '../DiscountRuleDropdown';
import {
  flashWarning,
  getCustomerTypesList,
  getCustomerTypesURL,
  postCustomerType,
} from '../utils';

interface Props {
  className?: string;
  initialCustomerTypes?: AutocompleteOption[];
  isDirtyOnChange?: boolean;
  isDisabled?: boolean;
  name: string;
  selectAllOption?: boolean;
  selectAllText?: string;
  selectAllValue?: number;
}

const DiscountRuleCustomerTypes: React.FC<Props> = ({
  className = '',
  initialCustomerTypes = [],
  isDirtyOnChange,
  isDisabled,
  name,
  selectAllOption = true,
  selectAllText = 'All customer types',
  selectAllValue,
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [customerTypes, setCustomerTypes] = React.useState<AutocompleteOption[]>([]);

  const handleOnCreateOption = async (
    newCustomerTypeName: string
  ): Promise<AutocompleteOption[]> => {
    const postResponse = await postCustomerType(newCustomerTypeName);

    if (postResponse) {
      const { CustomerType: customerTypeResponse } = postResponse;
      const newCustomerType = {
        label: newCustomerTypeName,
        value: parseInt(customerTypeResponse.customerTypeID, 10),
      };

      setCustomerTypes(previousCustomerTypes => [...previousCustomerTypes, newCustomerType]);

      return [newCustomerType];
    }

    return [];
  };

  React.useEffect(() => {
    const fetchCustomerTypes = async () => {
      try {
        const customerTypes = await window.merchantos.session.apiRequest(getCustomerTypesURL());
        const customerTypesList = getCustomerTypesList(customerTypes);
        setCustomerTypes(customerTypesList);
      } catch (error) {
        flashWarning('Customer types fetching failed', error);
      }
      setIsLoading(false);
    };

    fetchCustomerTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DiscountRuleDropdown
      className={className}
      initialOptions={initialCustomerTypes}
      isDirtyOnChange={isDirtyOnChange}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isMulti={true}
      name={name}
      onCreateOption={handleOnCreateOption}
      options={customerTypes}
      placeholder="Select one or more customer types"
      selectAllOption={selectAllOption}
      selectAllText={selectAllText}
      selectAllValue={selectAllValue}
    />
  );
};

const RenderedComponent = createReactRenderer({
  component: DiscountRuleCustomerTypes,
  selector: '[data-react-root="DiscountRuleCustomerTypes"]',
});

window.history.onChangeState(
  {
    id: undefined,
    name: 'discount_rule.views.discount_rule',
  },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default DiscountRuleCustomerTypes;
